import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, ChevronRight, Film, Layers, Plus, Sparkles, Star } from 'lucide-react';

import {
  useGetGalleryQuery,
  useGetMyAvatarsQuery,
  type AvatarItem,
} from '@/services/avatarApi';
import { AvatarCard } from '@/components/avatars/AvatarCard';
import { AvatarCarousel } from '@/components/avatars/AvatarCarousel';
import { AvatarSkeleton } from '@/components/avatars/AvatarSkeleton';
import {
  AvatarFilters,
  applyAvatarFilter,
  type AvatarFilter,
  type AvatarView,
} from '@/components/avatars/AvatarFilters';
import { useMainButton } from '@/hooks/useMainButton';
import { useHaptics } from '@/hooks/useHaptics';
import { cn } from '@/lib/cn';

type Tab = 'mine' | 'explore';

const VIEW_STORAGE_KEY = 'qlash.avatar.view';
const FILTER_STORAGE_KEY = 'qlash.avatar.filter';

function readPersisted<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  const v = window.localStorage.getItem(key);
  return v && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

export function Avatars() {
  const navigate = useNavigate();
  const haptics = useHaptics();
  // 2026-05-20 — default to My Avatars (Luca's preference: users care about
  // their own work first; Explore is secondary). Explore stays as a tap-away.
  const [activeTab, setActiveTab] = useState<Tab>('mine');
  const [view, setView] = useState<AvatarView>(() =>
    readPersisted(VIEW_STORAGE_KEY, ['grid', 'list'] as const, 'grid'),
  );
  const [filter, setFilter] = useState<AvatarFilter>(() =>
    readPersisted(FILTER_STORAGE_KEY, ['all', '3d', 'animated', 'featured', 'photo'] as const, 'all'),
  );

  useEffect(() => {
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, view); } catch { /* ignore */ }
  }, [view]);
  useEffect(() => {
    try { window.localStorage.setItem(FILTER_STORAGE_KEY, filter); } catch { /* ignore */ }
  }, [filter]);

  const galleryQuery = useGetGalleryQuery({ page: 1, limit: 50 });
  const myQuery = useGetMyAvatarsQuery({ page: 1, limit: 50 });

  const data = activeTab === 'explore' ? galleryQuery.data : myQuery.data;
  const isLoading = activeTab === 'explore' ? galleryQuery.isLoading : myQuery.isLoading;
  const isError = activeTab === 'explore' ? galleryQuery.isError : myQuery.isError;
  const allItems = data?.items ?? [];

  // The filter bar only applies to the "mine" tab. Explore stays a flat feed.
  const filteredItems = useMemo(
    () => (activeTab === 'mine' ? applyAvatarFilter(allItems, filter) : allItems),
    [activeTab, allItems, filter],
  );

  const switchTab = useCallback(
    (tab: Tab) => {
      if (tab !== activeTab) {
        haptics.selection();
        setActiveTab(tab);
      }
    },
    [activeTab, haptics],
  );

  const onFilterChange = useCallback(
    (f: AvatarFilter) => {
      haptics.selection();
      setFilter(f);
    },
    [haptics],
  );

  const onViewChange = useCallback(
    (v: AvatarView) => {
      haptics.selection();
      setView(v);
    },
    [haptics],
  );

  const goCreate = useCallback(() => {
    haptics.impact('light');
    navigate('/avatars/new');
  }, [navigate, haptics]);

  useMainButton({
    text: 'Create avatar',
    onClick: goCreate,
  });

  return (
    <div className="flex flex-col gap-4 px-4 pb-24 pt-5">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-fg">Avatars</h1>
          <p className="mt-1 text-sm text-fg-muted">
            AI-generated identities for your QLASH profile.
          </p>
        </div>
        <button
          type="button"
          onClick={goCreate}
          aria-label="Create avatar"
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
            'bg-brand text-brand-fg transition-opacity active:opacity-80',
          )}
        >
          <Plus aria-hidden className="h-5 w-5" />
        </button>
      </header>

      {/* Entry point to the wizard-style Avatar Editor. Moved here from the
          Hub (UX decision 2026-05-26) so the editor lives inside the avatar
          section rather than as a Hub CTA. */}
      <Link
        to="/avatars/editor"
        className="flex items-center justify-between rounded-2xl border border-dashed border-brand/40 bg-brand/5 px-4 py-3 text-sm transition-opacity active:opacity-80"
      >
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Film aria-hidden className="h-5 w-5" />
          </span>
          <div className="flex flex-col">
            <span className="font-bold text-fg">Avatar Editor</span>
            <span className="text-[11px] text-fg-muted">
              Guided wizard: image → motion → final clip
            </span>
          </div>
        </div>
        <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-brand">
          beta
        </span>
      </Link>

      <div role="tablist" className="flex gap-1 rounded-xl bg-bg-secondary p-1">
        {(['mine', 'explore'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => switchTab(tab)}
            className={cn(
              'flex-1 rounded-lg py-2 text-sm font-semibold transition-colors',
              activeTab === tab
                ? 'bg-brand text-brand-fg'
                : 'bg-transparent text-fg-muted',
            )}
          >
            {tab === 'mine' ? 'My avatars' : 'Explore'}
          </button>
        ))}
      </div>

      {activeTab === 'mine' && allItems.length > 0 && (
        <AvatarFilters
          items={allItems}
          activeFilter={filter}
          view={view}
          onFilterChange={onFilterChange}
          onViewChange={onViewChange}
        />
      )}

      {isLoading && <AvatarSkeleton />}

      {isError && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
          Failed to load avatars.
        </div>
      )}

      {!isLoading && !isError && filteredItems.length === 0 && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          {activeTab === 'explore' ? (
            'No avatars yet. Be the first to create one!'
          ) : allItems.length === 0 ? (
            "You haven't created any avatars yet."
          ) : (
            <>No avatars match this filter. <button type="button" onClick={() => onFilterChange('all')} className="text-brand underline">Show all</button>.</>
          )}
          {allItems.length === 0 && (
            <Link
              to="/avatars/new"
              className="mt-3 inline-block rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-brand-fg"
            >
              Create your first
            </Link>
          )}
        </div>
      )}

      {filteredItems.length > 0 && (
        activeTab === 'mine' ? (
          // 2026-05-20 — "My avatars" lists ONE compact row per series. Tap to
          // drill into all variations. Replaces the per-variation flat stack
          // because that doesn't scale once a user has 10+ avatars × 3-5 variants
          // each. Statistics chips (count, 3D, animated, skins, video clips,
          // featured) summarize the series at a glance.
          <div className="flex flex-col gap-2">
            {groupBySeries(filteredItems).map((series) => (
              <SeriesRow
                key={series.seriesId}
                series={series}
                onOpen={() => { haptics.impact('light'); navigate(`/avatars/${series.latest._id}/series`); }}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {filteredItems.map((avatar) => renderAvatar(avatar))}
          </div>
        )
      )}
    </div>
  );
}

function renderAvatar(avatar: AvatarItem) {
  const versions = avatar.seriesCount ?? 1;
  if (versions > 1) {
    return <AvatarCarousel key={avatar._id} avatar={avatar} />;
  }
  return <AvatarCard key={avatar._id} avatar={avatar} />;
}

interface SeriesGroup {
  seriesId: string;
  latest: AvatarItem;
  variants: AvatarItem[];
  count: number;
  /** Aggregated capabilities across all variants in the series. */
  has3d: boolean;
  hasAnimated: boolean;
  hasSkins: boolean;
  hasVideoClips: boolean;
  hasFeatured: boolean;
}

/**
 * Group a flat list of avatars by seriesId. Picks the latest (newest createdAt)
 * as the series cover. `count` is the visible-from-this-page count — which may
 * be lower than `latest.seriesCount` (server-side total) when the user has more
 * variants than the current `limit`. We surface BOTH so the user knows when to
 * drill in for the rest.
 */
function groupBySeries(items: AvatarItem[]): SeriesGroup[] {
  const byId = new Map<string, SeriesGroup>();
  for (const a of items) {
    const sid = a.seriesId;
    const entry = byId.get(sid);
    if (entry) {
      entry.variants.push(a);
      if (new Date(a.createdAt) > new Date(entry.latest.createdAt)) entry.latest = a;
      entry.has3d        ||= a.mesh?.status === 'ready' || a.mesh?.status === 'animated_ready';
      entry.hasAnimated  ||= a.mesh?.status === 'animated_ready' || ((a.mesh?.animationPacks?.length ?? 0) > 0);
      entry.hasSkins     ||= (a.mesh?.skins?.length ?? 0) > 0;
      entry.hasVideoClips ||= (a.videoClips?.length ?? 0) > 0;
      entry.hasFeatured  ||= !!a.isFeatured;
    } else {
      byId.set(sid, {
        seriesId: sid,
        latest: a,
        variants: [a],
        count: 0, // filled below from latest.seriesCount
        has3d: a.mesh?.status === 'ready' || a.mesh?.status === 'animated_ready',
        hasAnimated: a.mesh?.status === 'animated_ready' || ((a.mesh?.animationPacks?.length ?? 0) > 0),
        hasSkins: (a.mesh?.skins?.length ?? 0) > 0,
        hasVideoClips: (a.videoClips?.length ?? 0) > 0,
        hasFeatured: !!a.isFeatured,
      });
    }
  }
  // Prefer server-side seriesCount when available — handles partial pagination.
  const list = Array.from(byId.values()).map((s) => ({
    ...s,
    count: s.latest.seriesCount ?? s.variants.length,
  }));
  // Newest series first by latest.createdAt.
  list.sort((a, b) => new Date(b.latest.createdAt).getTime() - new Date(a.latest.createdAt).getTime());
  return list;
}

interface SeriesRowProps {
  series: SeriesGroup;
  onOpen: () => void;
}

function SeriesRow({ series, onOpen }: SeriesRowProps) {
  const { latest, count, has3d, hasAnimated, hasSkins, hasVideoClips, hasFeatured } = series;
  const sub = `${count} ${count === 1 ? 'variant' : 'variants'} · ${new Date(latest.createdAt).toLocaleDateString()}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex items-center gap-3 rounded-2xl border border-border bg-bg-secondary p-2 text-left',
        'transition-opacity active:opacity-80',
      )}
    >
      <img
        src={latest.thumbnailUrl ?? latest.imageUrl}
        alt={latest.title}
        className="h-14 w-14 shrink-0 rounded-xl border border-black/40 bg-black object-cover"
      />
      <div className="min-w-0 flex-1">
        <p className="line-clamp-1 text-sm font-bold text-fg">{latest.title}</p>
        <p className="mt-0.5 line-clamp-1 text-[11px] text-fg-muted">{sub}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {hasFeatured && (
            <span className="flex items-center gap-1 rounded-full bg-brand/20 px-1.5 py-0.5 text-[10px] font-semibold text-brand">
              <Star aria-hidden className="h-2.5 w-2.5 fill-current" /> Hub
            </span>
          )}
          {has3d && (
            <span className="flex items-center gap-1 rounded-full bg-bg px-1.5 py-0.5 text-[10px] font-semibold text-fg-muted">
              <Box aria-hidden className="h-2.5 w-2.5" /> 3D
            </span>
          )}
          {hasAnimated && (
            <span className="flex items-center gap-1 rounded-full bg-bg px-1.5 py-0.5 text-[10px] font-semibold text-fg-muted">
              <Layers aria-hidden className="h-2.5 w-2.5" /> Anim
            </span>
          )}
          {hasSkins && (
            <span className="flex items-center gap-1 rounded-full bg-bg px-1.5 py-0.5 text-[10px] font-semibold text-fg-muted">
              <Sparkles aria-hidden className="h-2.5 w-2.5" /> Skins
            </span>
          )}
          {hasVideoClips && (
            <span className="flex items-center gap-1 rounded-full bg-bg px-1.5 py-0.5 text-[10px] font-semibold text-fg-muted">
              <Film aria-hidden className="h-2.5 w-2.5" /> Video
            </span>
          )}
        </div>
      </div>
      <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-fg-muted" />
    </button>
  );
}
